import { i18n } from "@/boot/i18n";
import { BLOCKRULE_TYPE } from "@/consts/survey";
import BlockruleService from "@/services/blockrule";
import ErrorHandler from "@/tools/errorHandler";
import { NOT_NULL, REQUIRED } from "../validator/methods";
import * as types from "./mutationTypes";

export default {
  initListState({ commit }) {
    commit(types.SET_BLOCKRULES, []);
    commit(types.SET_TOTAL_BLOCKRULES, 0);
  },
  clearListState({ commit }) {
    commit(types.CLEAR_BLOCKRULE);
    commit(types.CLEAR_BLOCKRULES);
    commit(types.CLEAR_TOTAL_BLOCKRULES);
  },
  initNewState({ dispatch }) {
    const validator = validators();
    dispatch("validator/addValidator", validator.nameValidator, { root: true });
    dispatch("validator/addValidator", validator.typeValidator, { root: true });
    dispatch("validator/addValidator", validator.ruleValidator, { root: true });
  },
  initCustomersBlockedState({ commit }) {
    commit(types.SET_CUSTOMERS_BLOCKED, []);
    commit(types.SET_TOTAL_CUSTOMERS_BLOCKED, 0);
  },
  clearCustomersBlockedState({ commit }) {
    commit(types.CLEAR_CUSTOMERS_BLOCKED);
    commit(types.CLEAR_TOTAL_CUSTOMERS_BLOCKED);
  },
  clearNewState({ dispatch, commit }) {
    commit(types.CLEAR_BLOCKRULE);
    dispatch("validator/clearValidator", null, { root: true });
  },
  setBlockrulesSuccess({ commit }, { blockrules, total }) {
    commit(types.SET_BLOCKRULES, blockrules);
    commit(types.SET_TOTAL_BLOCKRULES, total);
  },
  setCustomersBlockedSuccess({ commit }, { customers, total }) {
    commit(types.SET_CUSTOMERS_BLOCKED, customers);
    commit(types.SET_TOTAL_CUSTOMERS_BLOCKED, total);
  },
  async getAll({ rootState, dispatch }, pagination) {
    try {
      const response = await BlockruleService.getAll(rootState.organization.organization.uuid, pagination);
      if (response.data.data.blockrules) {
        dispatch("setBlockrulesSuccess", {
          blockrules: response.data.data.blockrules,
          total: response.data.data.total_rows
        });
      } else {
        dispatch("setBlockrulesSuccess", { blockrules: [], total: 0 });
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async create({ rootState, state, dispatch }) {
    try {
      await BlockruleService.create(rootState.organization.organization.uuid, state.blockrule);
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async update({ rootState, dispatch, state }) {
    try {
      await BlockruleService.update(rootState.organization.organization.uuid, state.blockrule);
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async save({ state, dispatch }) {
    const valid = await dispatch("validate");
    if (!valid) {
      return;
    }
    let saved = false;
    if (state.blockrule.uuid) {
      saved = await dispatch("update");
    } else {
      saved = await dispatch("create");
    }
    if (saved) {
      dispatch("ui/showToastSuccess", i18n.t("blockrule.messages.create_success"), { root: true });
    }
    return saved;
  },
  async delete({ rootState, dispatch }, blockruleUUID) {
    try {
      await BlockruleService.delete(rootState.organization.organization.uuid, blockruleUUID);
      dispatch("ui/showToastSuccess", i18n.t("blockrule.messages.remove_success"), { root: true });
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async getCustomersBlocked({ rootState, dispatch }, { blockruleUUID, pagination }) {
    try {
      const response = await BlockruleService.getCustomersBloked(
        rootState.organization.organization.uuid,
        blockruleUUID,
        pagination
      );

      if (response.data.data.customers) {
        dispatch("setCustomersBlockedSuccess", {
          customers: response.data.data.customers,
          total: response.data.data.total_rows
        });
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  setBlockrule({ commit }, blockrule) {
    commit(types.SET_BLOCKRULE, blockrule);
  },
  clearBlockrule({ commit }) {
    commit(types.CLEAR_BLOCKRULE);
  },
  removeBlockrule({ commit }, uuid) {
    commit(types.REMOVE_BLOCKRULE, uuid);
  },
  setField({ commit, dispatch }, { field, value }) {
    commit(types.SET_FIELD, { field, value });
    dispatch("validator/checkValidation", { field, value }, { root: true });
  },
  async validate({ rootState, state, dispatch }) {
    await dispatch("validator/validate", { field: "name", value: state.blockrule.name }, { root: true });
    await dispatch("validator/validate", { field: "type", value: state.blockrule.type }, { root: true });
    const ruleCustomMessage =
      state.blockrule.type === BLOCKRULE_TYPE.EMAIL
        ? i18n.t("blockrule.new.blockrules.email")
        : i18n.t("blockrule.new.blockrules.phone");
    await dispatch(
      "validator/validate",
      {
        field: "rule",
        value: { type: state.blockrule.type, value: state.blockrule.rule },
        customMessage: ruleCustomMessage
      },
      { root: true }
    );
    return rootState.validator.errors.length === 0;
  }
};

const validators = () => {
  return {
    nameValidator: {
      field: "name",
      validations: [{ method: REQUIRED, message: i18n.t("general.rules.required") }]
    },
    typeValidator: {
      field: "type",
      validations: [{ method: NOT_NULL, message: i18n.t("general.rules.required") }]
    },
    ruleValidator: {
      field: "rule",
      validations: [{ method: validateRule, message: i18n.t("blockrule.new.blockrules.email") }]
    }
  };
};

function validateRule({ type, value }) {
  if (type === BLOCKRULE_TYPE.EMAIL) {
    return isEmailBlockrule(value);
  } else {
    return isPhoneBlockrule(value);
  }
}
function isPhoneBlockrule(v) {
  if (!v) {
    return false;
  }
  const hasOnlyNumericAndAsterisk = /^[\d*]*$/.test(v);

  return hasOnlyNumericAndAsterisk;
}
function isEmailBlockrule(v) {
  if (!v) {
    return false;
  }
  const hasOnlyAlphanumericAsteriskAndDot = /^[\w*.!#$%&'*+-/=?^_`{|}~]*@.*$/.test(v);
  const hasOnlyOneAt = (v.match(/@/g) || []).length === 1;

  return hasOnlyAlphanumericAsteriskAndDot && hasOnlyOneAt;
}
